import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CalendarClock, CalendarCheck, MapPin, Briefcase, GraduationCap, Users, BookOpen, FileText, Plus, Clock, CalendarPlus } from "lucide-react";
import { api } from "@/lib/api";
import { formatTime, getTimeFormat } from "@/lib/time";
import { downloadIcs } from "@/lib/ics";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const FALLBACK = [30.6146, -96.3417];

const CAT = {
  job_fair: { label: "Career fair", icon: Briefcase },
  office_hours: { label: "Office hours", icon: GraduationCap },
  meeting: { label: "Advising", icon: Users },
  study_group: { label: "Study group", icon: BookOpen },
  exam: { label: "Exam", icon: FileText },
  other: { label: "Appointment", icon: CalendarClock },
};

function nextOccurrence(ev) {
  const [h, m] = (ev.start_time || "00:00").split(":").map(Number);
  if (ev.event_date) {
    const d = new Date(ev.event_date + "T00:00");
    d.setHours(h || 0, m || 0, 0, 0);
    return d;
  }
  const now = new Date();
  let best = null;
  for (const dname of ev.days || []) {
    const idx = DOW.indexOf(dname);
    if (idx < 0) continue;
    const cand = new Date(now);
    cand.setDate(now.getDate() + ((idx - now.getDay() + 7) % 7));
    cand.setHours(h || 0, m || 0, 0, 0);
    if (cand < now) cand.setDate(cand.getDate() + 7);
    if (!best || cand < best) best = cand;
  }
  return best;
}

const isToday = (d) => d.toDateString() === new Date().toDateString();

function whenLabel(d) {
  const now = new Date();
  const same = (a, b) => a.toDateString() === b.toDateString();
  const tmrw = new Date(now);
  tmrw.setDate(now.getDate() + 1);
  if (same(d, now)) return "Today";
  if (same(d, tmrw)) return "Tomorrow";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

// Live "leave in X" status for today's appointments, travel-aware when we know the walk time.
function cardStatus(when, travelMin, nowSec) {
  const startSec = when.getHours() * 3600 + when.getMinutes() * 60;
  if (travelMin != null) {
    if (nowSec >= startSec) return { text: "Started", tone: "muted" };
    const diff = startSec - travelMin * 60 - nowSec;
    if (diff <= 0) return { text: "Head out now", tone: "urgent" };
    const mm = Math.floor(diff / 60);
    const ss = diff % 60;
    const disp = mm >= 60 ? `${Math.round(diff / 60)} min` : `${mm}:${String(ss).padStart(2, "0")}`;
    return { text: `Leave in ${disp}`, tone: diff <= 300 ? "urgent" : "brand" };
  }
  const diff = startSec - nowSec;
  if (diff <= 0) return { text: "Started", tone: "muted" };
  const h = Math.floor(diff / 3600);
  const mm = Math.floor((diff % 3600) / 60);
  return { text: h > 0 ? `in ${h}h ${mm}m` : `in ${mm}m`, tone: "brand" };
}

const TONE = {
  brand: "text-brand bg-brand/10",
  urgent: "text-[#b45309] bg-[#F59E0B]/15",
  muted: "text-muted-foreground bg-secondary",
};

export default function AppointmentsStrip({ classes, buildings = [], onPickDay, userLoc, pref }) {
  const fmt = getTimeFormat();
  const [travel, setTravel] = useState({});
  const [nowSec, setNowSec] = useState(() => {
    const d = new Date();
    return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
  });

  const items = useMemo(() => {
    const now = new Date();
    return (classes || [])
      .filter((c) => c.kind === "event")
      .map((c) => ({ ...c, when: nextOccurrence(c) }))
      .filter((c) => c.when)
      .filter((c) => !c.event_date || new Date(c.event_date + "T23:59") >= now)
      .sort((a, b) => a.when - b.when)
      .slice(0, 8);
  }, [classes]);

  const hasToday = items.some((c) => isToday(c.when));

  useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      setNowSec(d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds());
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Pull today's plan so we know the walk/bus time to each appointment for the countdown.
  useEffect(() => {
    if (!hasToday) { setTravel({}); return; }
    const day = DOW[new Date().getDay()];
    if (!WEEKDAYS.includes(day)) return;
    const loc = userLoc || FALLBACK;
    api.get(`/plan?day=${day}&lat=${loc[0]}&lng=${loc[1]}&pref=${pref || ""}`)
      .then((r) => {
        const m = {};
        (r.data.classes || []).forEach((c, i) => { m[c.id] = r.data.legs?.[i]?.travel?.best_min; });
        setTravel(m);
      })
      .catch(() => {});
  }, [hasToday, userLoc?.[0], userLoc?.[1], pref]);

  if (items.length === 0) return null;

  const addAppointment = () => {
    window.dispatchEvent(new CustomEvent("routerev:add-event"));
    document.querySelector("[data-testid='add-event-btn']")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="mb-5" data-testid="appointments-strip">
      <div className="flex items-center gap-1.5 mono text-[11px] tracking-[0.2em] uppercase text-brand mb-2">
        <CalendarCheck size={12} strokeWidth={1.5} /> My appointments
        <span className="text-muted-foreground normal-case tracking-normal">· {items.length} upcoming</span>
      </div>
      <div className="flex gap-3 overflow-x-auto hide-scrollbar pb-1">
        {items.map((c, i) => {
          const meta = CAT[c.category] || CAT.other;
          const Icon = meta.icon;
          const b = buildings.find((x) => x.id === c.building_id);
          const place = b?.name || c.location || c.name;
          const wd = DOW[c.when.getDay()];
          const status = isToday(c.when) ? cardStatus(c.when, travel[c.id], nowSec) : null;
          return (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              data-testid={`appointment-card-${c.id}`}
              onClick={() => WEEKDAYS.includes(wd) && onPickDay?.(wd)}
              className="shrink-0 w-56 bg-white border border-border rounded-xl p-3.5 hover:border-brand hover:shadow-sm transition-all cursor-pointer"
            >
              <div className="flex items-center gap-1.5 mono text-[11px] font-bold uppercase tracking-widest text-[#92400e] bg-[#F59E0B]/15 rounded px-1.5 py-0.5 w-fit">
                <Icon size={10} strokeWidth={2} /> {meta.label}
              </div>
              <div className="font-semibold text-sm truncate mt-2">{c.name}</div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground truncate mt-1">
                <MapPin size={11} strokeWidth={1.6} className="shrink-0" /> {place}
              </div>
              <div className="mono text-xs font-bold text-brand mt-2">
                {whenLabel(c.when)} · {formatTime(c.start_time, fmt)}
              </div>
              <div className="flex items-center justify-between gap-2 mt-2">
                <div className="min-w-0">
                  {status && (
                    <span
                      data-testid={`appointment-countdown-${c.id}`}
                      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-bold tabular-nums ${TONE[status.tone]}`}
                    >
                      <Clock size={11} strokeWidth={2} /> {status.text}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const start = c.when;
                      const [eh, em] = (c.end_time || c.start_time || "00:00").split(":").map(Number);
                      const end = new Date(start); end.setHours(eh || 0, em || 0, 0, 0);
                      if (end <= start) end.setTime(start.getTime() + 30 * 60000);
                      downloadIcs({ uid: c.id, title: c.name, start, end, location: place, url: c.booking_url, alarmMin: 30 });
                    }}
                    data-testid={`appointment-ics-${c.id}`}
                    title="Add this appointment to your calendar (.ics)"
                    className="flex items-center gap-1 text-[11px] font-semibold text-brand bg-brand/10 hover:bg-brand/20 rounded-md px-2 py-1 transition-colors"
                  >
                    <CalendarPlus size={12} strokeWidth={1.9} /> Add
                  </button>
                  {c.booking_url && (
                    <a
                      href={c.booking_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`appointment-book-${c.id}`}
                      className="flex items-center gap-1 text-[11px] font-semibold text-[#0891b2] bg-[#0891b2]/10 hover:bg-[#0891b2]/20 rounded-md px-2 py-1 transition-colors"
                    >
                      <CalendarCheck size={12} strokeWidth={1.9} /> Book
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
        <button
          data-testid="strip-add-appointment"
          onClick={addAppointment}
          className="shrink-0 w-40 rounded-xl border border-dashed border-brand/40 bg-brand/[0.03] text-brand hover:bg-brand/10 hover:border-brand transition-colors flex flex-col items-center justify-center gap-1.5 py-4"
        >
          <span className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center">
            <Plus size={16} strokeWidth={2.2} />
          </span>
          <span className="text-xs font-semibold">Add appointment</span>
        </button>
      </div>
    </div>
  );
}
